import type {
  AccountDTO,
  ArticleIdeaDTO,
  ArticleIdeaStatus,
  BrandUserDTO,
  ChangePasswordInput,
  ConnectSocialCredentialsInput,
  ConnectWordpressInput,
  ContentMode,
  CreateBrandInput,
  CreateBrandUserInput,
  DashboardDTO,
  ResetPasswordInput,
  UpdateBrandInput,
  DraftDTO,
  DraftRevisionDTO,
  DraftStatus,
  GenerateCustomInput,
  GenerateDraftInput,
  HighlightDigestDTO,
  HolidayDTO,
  ImageSource,
  MarketingMaterialDTO,
  MaterialAttachmentDTO,
  MaterialGenerateInput,
  MediaAssetDTO,
  PublishWpInput,
  ReviseDraftInput,
  SessionUser,
  SocialConnectionDTO,
  DraftPublicationDTO,
  UpdateArticleInput,
  UpdateDraftInput,
  UpdateMediaInput,
  VideoGenerateInput,
  VideoProjectDTO,
} from "@app/shared";

/**
 * Base URL of the @app/api server. In production the SPA is served by the API
 * itself (single origin), so the base is "" (same-origin relative paths). In dev
 * the Vite server is a separate origin, so it defaults to localhost:3000.
 * `VITE_API_URL` overrides either. Trailing slashes are trimmed.
 */
export const API_BASE_URL = (
  import.meta.env.VITE_API_URL ??
  (import.meta.env.PROD ? "" : "http://localhost:3000")
).replace(/\/+$/, "");

/** Error thrown by the fetch wrapper carrying the HTTP status + parsed body. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Global handler invoked when an authenticated data request returns 401 (the
 * session expired or was revoked server-side). The app registers a callback
 * via {@link onAuthExpired} so it can clear the cached session and redirect to
 * /login. The probes that establish auth state (`/auth/me`, `/auth/login`) are
 * excluded so we never trigger a redirect loop from the login flow itself.
 */
type AuthExpiredHandler = () => void;
let authExpiredHandler: AuthExpiredHandler | null = null;

/** Register the global 401 → logout/redirect handler. Returns an unsubscribe fn. */
export function onAuthExpired(handler: AuthExpiredHandler): () => void {
  authExpiredHandler = handler;
  return () => {
    if (authExpiredHandler === handler) authExpiredHandler = null;
  };
}

/** Paths whose 401s establish auth state and must NOT trigger the global handler. */
const AUTH_PROBE_PATHS = ["/auth/me", "/auth/login"];

/**
 * Thin fetch wrapper. Always sends credentials (httpOnly session cookie),
 * JSON-encodes bodies, and throws {@link ApiError} on non-2xx responses.
 * Returns `undefined` for 204 / empty responses.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, signal } = options;

  // `ngrok-skip-browser-warning` makes ngrok's free-tier serve the real response
  // to XHR/fetch instead of its HTML interstitial. Harmless on other hosts.
  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "true",
  };
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: payload,
    credentials: "include",
    signal,
  });

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (isRecord(parsed) && typeof parsed.message === "string"
        ? parsed.message
        : undefined) ??
      (isRecord(parsed) && typeof parsed.error === "string"
        ? parsed.error
        : undefined) ??
      res.statusText ??
      `Request failed (${res.status})`;

    // Global 401 handling: an authenticated data request was rejected because
    // the session is gone. Notify the app so it can clear the cached session
    // and redirect to /login. Auth probes (`/auth/me`, `/auth/login`) are
    // excluded — their 401s are expected and handled locally — to avoid loops.
    if (
      res.status === 401 &&
      authExpiredHandler &&
      !AUTH_PROBE_PATHS.some((probe) => path.startsWith(probe))
    ) {
      authExpiredHandler();
    }

    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Image URL for a draft preview — ALWAYS same-origin relative
 * (`/public/images/<id>.png`). We intentionally ignore the stored
 * `publicImageUrl`: that is an absolute host meant for Meta's server-side fetch
 * and can be a stale/localhost host that breaks (mixed-content) when the app is
 * served over https (e.g. ngrok). Missing files 404 → ImagePreview placeholder.
 */
export function draftImageUrl(draft: Pick<DraftDTO, "id" | "updatedAt">): string {
  // `?v=updatedAt` busts the browser cache whenever the image is re-rendered, so
  // tweaks (logo size, theme, etc.) show up without a manual hard-refresh.
  return `${API_BASE_URL}/public/images/${draft.id}.png?v=${encodeURIComponent(draft.updatedAt)}`;
}

/**
 * Image URL for a specific draft VERSION's permanent per-version file
 * (`/public/images/<draftId>-v<version>.png`). Used by the "Riwayat versi"
 * history thumbnails. The current image still uses {@link draftImageUrl}.
 */
export function revisionImageUrl(draftId: string, version: number): string {
  return `${API_BASE_URL}/public/images/${draftId}-v${version}.png`;
}

/**
 * Image URL for a gallery asset — same-origin relative
 * (`/public/media/<filename>`). `?v=updatedAt` busts the browser cache when the
 * asset's metadata changes (the file itself is immutable per id).
 */
export function mediaImageUrl(
  asset: Pick<MediaAssetDTO, "filename" | "updatedAt">,
): string {
  return `${API_BASE_URL}/public/media/${asset.filename}?v=${encodeURIComponent(asset.updatedAt)}`;
}

/**
 * Same-origin URL for a brand's logo (`/public/brand-logos/<key>`). Pass a
 * `bust` token (e.g. a counter or timestamp) to defeat the cache after a change.
 */
export function brandLogoUrl(key: string, bust?: string | number): string {
  const q = bust !== undefined ? `?v=${encodeURIComponent(String(bust))}` : "";
  return `${API_BASE_URL}/public/brand-logos/${key}${q}`;
}

/**
 * Same-origin URL for a marketing material's rendered PNG preview
 * (`/public/materials/<id>.png`). `?v=updatedAt` busts the cache when the
 * material is (re)rendered.
 */
export function materialImageUrl(
  material: Pick<MarketingMaterialDTO, "id" | "updatedAt">,
): string {
  return `${API_BASE_URL}/public/materials/${material.id}.png?v=${encodeURIComponent(material.updatedAt)}`;
}

/** Same-origin URL for a marketing material's print-ready PDF. */
export function materialPdfUrl(id: string): string {
  return `${API_BASE_URL}/public/materials/${id}.pdf`;
}

/** Same-origin URL for a generated video's MP4 (`?v=updatedAt` busts cache). */
export function videoUrl(
  v: Pick<VideoProjectDTO, "id" | "updatedAt">,
): string {
  return `${API_BASE_URL}/public/videos/${v.id}.mp4?v=${encodeURIComponent(v.updatedAt)}`;
}

/** Same-origin URL for a brief attachment (image preview / text). */
export function materialAttachmentUrl(
  att: Pick<MaterialAttachmentDTO, "id" | "ext">,
): string {
  return `${API_BASE_URL}/public/material-attachments/${att.id}.${att.ext}`;
}

/**
 * Create a marketing material. With brief attachments, sends multipart/form-data
 * (a raw fetch so the browser sets the boundary); without, sends JSON.
 */
export async function generateMaterialRequest(
  input: MaterialGenerateInput,
  files: File[] = [],
): Promise<MarketingMaterialDTO> {
  if (files.length === 0) {
    return apiFetch<MarketingMaterialDTO>("/materials/generate", {
      method: "POST",
      body: input,
    });
  }
  const form = new FormData();
  form.append("accountKey", input.accountKey);
  form.append("format", input.format);
  form.append("brief", input.brief);
  if (input.withPhoto !== undefined)
    form.append("withPhoto", String(input.withPhoto));
  if (input.imageSource) form.append("imageSource", input.imageSource);
  if (input.imageQuery) form.append("imageQuery", input.imageQuery);
  if (input.customWidthCm !== undefined)
    form.append("customWidthCm", String(input.customWidthCm));
  if (input.customHeightCm !== undefined)
    form.append("customHeightCm", String(input.customHeightCm));
  for (const f of files) form.append("file", f);

  const res = await fetch(`${API_BASE_URL}/materials/generate`, {
    method: "POST",
    headers: { "ngrok-skip-browser-warning": "true" },
    body: form,
    credentials: "include",
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const message =
      (isRecord(parsed) && typeof parsed.error === "string"
        ? parsed.error
        : undefined) ?? `Generate failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as MarketingMaterialDTO;
}

/**
 * Absolute URL to the Instagram OAuth start endpoint for a specific brand
 * (admin-guarded, browser redirect). The brand is carried through the handshake.
 */
export function instagramStartUrl(accountKey: string): string {
  return `${API_BASE_URL}/auth/instagram/start?accountKey=${encodeURIComponent(accountKey)}`;
}

/**
 * Absolute URL to a platform's OAuth start endpoint for a brand (admin-guarded,
 * browser redirect). Used for the "oauth2" social platforms.
 */
export function socialOauthStartUrl(
  accountKey: string,
  platform: string,
): string {
  return `${API_BASE_URL}/accounts/${accountKey}/connections/${platform}/oauth/start`;
}

/**
 * Upload one gallery image (multipart/form-data). Uses a raw fetch (NOT the JSON
 * `apiFetch`) so the browser sets the multipart boundary itself. Always sends the
 * session cookie; throws {@link ApiError} on non-2xx.
 */
export async function uploadMedia(form: FormData): Promise<MediaAssetDTO> {
  const res = await fetch(`${API_BASE_URL}/media`, {
    method: "POST",
    headers: { "ngrok-skip-browser-warning": "true" },
    body: form,
    credentials: "include",
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const message =
      (isRecord(parsed) && typeof parsed.error === "string"
        ? parsed.error
        : undefined) ?? `Upload failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as MediaAssetDTO;
}

/** Upload (or replace) a brand's logo. Returns the updated AccountDTO. */
export async function uploadBrandLogo(
  key: string,
  file: File,
): Promise<AccountDTO> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API_BASE_URL}/accounts/${key}/logo`, {
    method: "POST",
    headers: { "ngrok-skip-browser-warning": "true" },
    body: form,
    credentials: "include",
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const message =
      (isRecord(parsed) && typeof parsed.error === "string"
        ? parsed.error
        : undefined) ?? `Upload failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed as AccountDTO;
}

// -------------------------------------------------------------------------
// Typed API surface used by routes / React Query hooks.
// -------------------------------------------------------------------------

export interface DraftFilters {
  accountKey?: string;
  status?: string;
  from?: string;
  to?: string;
  /** Restrict to a single content mode (reguler/special/article). */
  mode?: ContentMode;
}

/** Query filters for the highlight digest list (`GET /highlights`). */
export interface HighlightFilters {
  accountKey?: string;
  date?: string;
}

/** Query filters for the article idea list (`GET /articles/ideas`). */
export interface ArticleIdeaFilters {
  accountKey?: string;
  status?: ArticleIdeaStatus;
}

/** Query filters for the article draft list (`GET /articles`). */
export interface ArticleFilters {
  accountKey?: string;
  status?: DraftStatus;
}

export const api = {
  // --- auth ---
  me: (signal?: AbortSignal) =>
    apiFetch<SessionUser>("/auth/me", { signal }),
  login: (input: { email: string; password: string }) =>
    apiFetch<SessionUser>("/auth/login", { method: "POST", body: input }),
  logout: () => apiFetch<void>("/auth/logout", { method: "POST" }),
  /** Set a new password (clears the admin-reset "must change" flag). */
  changePassword: (input: ChangePasswordInput) =>
    apiFetch<SessionUser>("/auth/change-password", {
      method: "POST",
      body: input,
    }),

  // --- accounts ---
  accounts: (signal?: AbortSignal) =>
    apiFetch<AccountDTO[]>("/accounts", { signal }),
  account: (key: string, signal?: AbortSignal) =>
    apiFetch<AccountDTO>(`/accounts/${key}`, { signal }),
  /**
   * Connect (or re-connect) a WordPress site to an account. Admin-guarded on the
   * API side; the credentials are encrypted at rest and never returned.
   */
  connectWordpress: (key: string, input: ConnectWordpressInput) =>
    apiFetch<AccountDTO>(`/accounts/${key}`, {
      method: "PATCH",
      body: { wordpress: input },
    }),
  /** Create a new brand (admin-only on the API side). */
  createBrand: (input: CreateBrandInput) =>
    apiFetch<AccountDTO>("/accounts", { method: "POST", body: input }),
  /** Update a brand's personalization (admin-only). */
  updateBrand: (key: string, input: UpdateBrandInput) =>
    apiFetch<AccountDTO>(`/accounts/${key}`, { method: "PATCH", body: input }),
  /** Delete an (empty) brand (admin-only). */
  deleteBrand: (key: string) =>
    apiFetch<void>(`/accounts/${key}`, { method: "DELETE" }),
  /** Create a brand-scoped login for a brand (admin-only). */
  createBrandUser: (key: string, input: CreateBrandUserInput) =>
    apiFetch<{ id: string; email: string; role: string; accountKey: string }>(
      `/accounts/${key}/users`,
      { method: "POST", body: input },
    ),
  /** List a brand's logins (admin-only). */
  brandUsers: (key: string, signal?: AbortSignal) =>
    apiFetch<BrandUserDTO[]>(`/accounts/${key}/users`, { signal }),
  /** Per-brand social-connection status for every supported platform. */
  socialConnections: (key: string, signal?: AbortSignal) =>
    apiFetch<SocialConnectionDTO[]>(`/accounts/${key}/connections`, { signal }),
  /** Connect a credentials-based platform (e.g. Bluesky app-password). Admin-only. */
  connectSocialCredentials: (
    key: string,
    platform: string,
    input: ConnectSocialCredentialsInput,
  ) =>
    apiFetch<SocialConnectionDTO>(`/accounts/${key}/connections/${platform}`, {
      method: "POST",
      body: input,
    }),
  /** Disconnect a brand's social platform (admin-only). */
  disconnectSocial: (key: string, platform: string) =>
    apiFetch<void>(`/accounts/${key}/connections/${platform}`, {
      method: "DELETE",
    }),
  /**
   * Reset a brand login's password (admin-only). The user is then forced to set
   * their own password on next login.
   */
  resetBrandUserPassword: (
    key: string,
    userId: string,
    input: ResetPasswordInput,
  ) =>
    apiFetch<BrandUserDTO>(`/accounts/${key}/users/${userId}/reset-password`, {
      method: "POST",
      body: input,
    }),
  /** Upload (or replace) a brand's logo (admin-only). */
  uploadBrandLogo,
  /** Remove a brand's logo (admin-only). */
  deleteBrandLogo: (key: string) =>
    apiFetch<AccountDTO>(`/accounts/${key}/logo`, { method: "DELETE" }),

  // --- dashboard (admin) ---
  dashboard: (signal?: AbortSignal) =>
    apiFetch<DashboardDTO>("/dashboard", { signal }),

  // --- settings ---
  settings: (signal?: AbortSignal) =>
    apiFetch<SettingDTO[]>("/settings", { signal }),
  setting: (key: string, signal?: AbortSignal) =>
    apiFetch<SettingDTO>(`/settings/${encodeURIComponent(key)}`, { signal }),
  /** Upsert a setting value (admin-only on the API side). */
  updateSetting: (key: string, value: unknown) =>
    apiFetch<SettingDTO>(`/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: { value },
    }),
  /**
   * Resolve the global default hero-image source ("pexels" | "library").
   * Tolerant: returns "pexels" when the setting is absent (404) or invalid.
   */
  imageSourceDefault: async (signal?: AbortSignal): Promise<ImageSource> => {
    try {
      const s = await apiFetch<SettingDTO>("/settings/image_source", { signal });
      return s.value === "library" ? "library" : "pexels";
    } catch {
      return "pexels";
    }
  },

  // --- media gallery ---
  media: (
    filters: { accountKey?: string; includeInactive?: boolean } = {},
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    if (filters.includeInactive) qs.set("includeInactive", "true");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<MediaAssetDTO[]>(`/media${suffix}`, { signal });
  },
  uploadMedia,
  updateMedia: (id: string, input: UpdateMediaInput) =>
    apiFetch<MediaAssetDTO>(`/media/${id}`, { method: "PATCH", body: input }),
  deleteMedia: (id: string) =>
    apiFetch<void>(`/media/${id}`, { method: "DELETE" }),

  // --- marketing materials (offline collateral: flyer/banner/etc.) ---
  materials: (
    filters: { accountKey?: string } = {},
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<MarketingMaterialDTO[]>(`/materials${suffix}`, { signal });
  },
  material: (id: string, signal?: AbortSignal) =>
    apiFetch<MarketingMaterialDTO>(`/materials/${id}`, { signal }),
  generateMaterial: (input: MaterialGenerateInput, files: File[] = []) =>
    generateMaterialRequest(input, files),
  regenerateMaterial: (id: string) =>
    apiFetch<MarketingMaterialDTO>(`/materials/${id}/regenerate`, {
      method: "POST",
    }),
  deleteMaterial: (id: string) =>
    apiFetch<void>(`/materials/${id}`, { method: "DELETE" }),

  // --- educational videos (prototype) ---
  videos: (filters: { accountKey?: string } = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<VideoProjectDTO[]>(`/videos${suffix}`, { signal });
  },
  video: (id: string, signal?: AbortSignal) =>
    apiFetch<VideoProjectDTO>(`/videos/${id}`, { signal }),
  generateVideo: (input: VideoGenerateInput) =>
    apiFetch<VideoProjectDTO>("/videos/generate", {
      method: "POST",
      body: input,
    }),
  deleteVideo: (id: string) =>
    apiFetch<void>(`/videos/${id}`, { method: "DELETE" }),

  // --- drafts ---
  drafts: (filters: DraftFilters = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    if (filters.status) qs.set("status", filters.status);
    if (filters.from) qs.set("from", filters.from);
    if (filters.to) qs.set("to", filters.to);
    if (filters.mode) qs.set("mode", filters.mode);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<DraftDTO[]>(`/drafts${suffix}`, { signal });
  },
  draft: (id: string, signal?: AbortSignal) =>
    apiFetch<DraftDTO>(`/drafts/${id}`, { signal }),
  generateDraft: (input: GenerateDraftInput) =>
    apiFetch<GenerateDraftResponse>("/drafts/generate", {
      method: "POST",
      body: input,
    }),
  updateDraft: (id: string, input: UpdateDraftInput) =>
    apiFetch<DraftDTO>(`/drafts/${id}`, { method: "PATCH", body: input }),
  approveDraft: (id: string, opts?: { now?: boolean }) =>
    apiFetch<DraftDTO>(`/drafts/${id}/approve`, {
      method: "POST",
      body: opts?.now ? { now: true } : undefined,
    }),
  unscheduleDraft: (id: string) =>
    apiFetch<DraftDTO>(`/drafts/${id}/unschedule`, { method: "POST" }),
  deleteDraft: (id: string) =>
    apiFetch<void>(`/drafts/${id}`, { method: "DELETE" }),

  // --- social cross-posting (Bluesky/X/… per-brand connections) ---
  draftPublications: (id: string, signal?: AbortSignal) =>
    apiFetch<DraftPublicationDTO[]>(`/drafts/${id}/publications`, { signal }),
  publishSocial: (id: string, platforms: string[]) =>
    apiFetch<DraftPublicationDTO[]>(`/drafts/${id}/publish-social`, {
      method: "POST",
      body: { platforms },
    }),

  // --- AI revision + version history (IG drafts) ---
  reviseDraft: (id: string, input: ReviseDraftInput) =>
    apiFetch<{ enqueued: boolean; jobId: string | null }>(
      `/drafts/${id}/revise`,
      { method: "POST", body: input },
    ),
  revisions: (id: string, signal?: AbortSignal) =>
    apiFetch<DraftRevisionDTO[]>(`/drafts/${id}/revisions`, { signal }),
  restoreRevision: (id: string, version: number) =>
    apiFetch<DraftDTO>(`/drafts/${id}/revisions/${version}/restore`, {
      method: "POST",
    }),

  // --- holidays ---
  holidays: (signal?: AbortSignal) =>
    apiFetch<HolidayDTO[]>("/holidays", { signal }),

  // --- background generation status (for the "generating now" indicator) ---
  generateStatus: (signal?: AbortSignal) =>
    apiFetch<GenerationStatus>("/generate/status", { signal }),

  // --- special case: highlights ---
  highlights: (filters: HighlightFilters = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    if (filters.date) qs.set("date", filters.date);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<HighlightDigestDTO[]>(`/highlights${suffix}`, { signal });
  },
  highlight: (id: string, signal?: AbortSignal) =>
    apiFetch<HighlightDigestDTO>(`/highlights/${id}`, { signal }),
  refreshHighlights: (accountKey: string) =>
    apiFetch<TriggerResponse>("/highlights/refresh", {
      method: "POST",
      body: { accountKey },
    }),
  generateFromHighlight: (
    id: string,
    itemIndex: number,
    imageSource?: ImageSource,
  ) =>
    apiFetch<TriggerResponse>(`/highlights/${id}/generate`, {
      method: "POST",
      body: { itemIndex, imageSource },
    }),

  // --- custom: manual instruction-driven post ---
  generateCustom: (input: GenerateCustomInput) =>
    apiFetch<{ enqueued: boolean; jobId: string | null; accountKey: string }>(
      "/custom/generate",
      { method: "POST", body: input },
    ),

  // --- article: research + ideas ---
  researchArticles: (accountKey: string) =>
    apiFetch<TriggerResponse>("/articles/research", {
      method: "POST",
      body: { accountKey },
    }),
  articleIdeas: (filters: ArticleIdeaFilters = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    if (filters.status) qs.set("status", filters.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<ArticleIdeaDTO[]>(`/articles/ideas${suffix}`, { signal });
  },
  writeArticle: (ideaId: string) =>
    apiFetch<TriggerResponse>(`/articles/ideas/${ideaId}/write`, {
      method: "POST",
    }),
  dismissIdea: (id: string) =>
    apiFetch<ArticleIdeaDTO>(`/articles/ideas/${id}/dismiss`, {
      method: "POST",
    }),

  // --- article: drafts ---
  articles: (filters: ArticleFilters = {}, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    if (filters.accountKey) qs.set("accountKey", filters.accountKey);
    if (filters.status) qs.set("status", filters.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return apiFetch<DraftDTO[]>(`/articles${suffix}`, { signal });
  },
  article: (id: string, signal?: AbortSignal) =>
    apiFetch<DraftDTO>(`/articles/${id}`, { signal }),
  updateArticle: (id: string, input: UpdateArticleInput) =>
    apiFetch<DraftDTO>(`/articles/${id}`, { method: "PATCH", body: input }),
  publishArticle: (id: string, input: PublishWpInput) =>
    apiFetch<TriggerResponse>(`/articles/${id}/publish`, {
      method: "POST",
      body: input,
    }),
  deleteArticle: (id: string) =>
    apiFetch<void>(`/articles/${id}`, { method: "DELETE" }),
};

/**
 * Response from the async trigger routes (refresh highlights, generate from
 * highlight, research articles, write article, publish to WordPress). The route
 * returns 202 Accepted after enqueueing the BullMQ job; `jobId` is null when the
 * request was deduplicated / no job was enqueued.
 */
export interface TriggerResponse {
  enqueued: boolean;
  jobId: string | null;
}

/** A single Setting row (`/settings`). The stored value is opaque JSON. */
export interface SettingDTO {
  key: string;
  value: unknown;
}

/**
 * Response from `POST /drafts/generate` (202 Accepted). `count` is how many
 * day-jobs were enqueued (1 for single mode, N for a date range). `targetDate`
 * is present in single mode; `from`/`to` in range mode.
 */
export interface GenerateDraftResponse {
  enqueued: boolean;
  count: number;
  accountKey: string;
  jobId?: string | null;
  targetDate?: string;
  from?: string;
  to?: string;
}

/** Live background-generation activity from `GET /generate/status`. */
export interface GenerationStatus {
  /** Jobs currently processing. */
  active: number;
  /** Jobs queued and about to process. */
  waiting: number;
  /** True when anything is generating (active + waiting > 0). */
  busy: boolean;
  queues: Record<string, { active: number; waiting: number }>;
}
